import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, Save, Lock, LayoutDashboard, BarChart3, Users, 
  Filter, Clock, CheckCircle2, AlertTriangle, Shield, BookOpen,
  PieChart, LineChart, Table as TableIcon, HelpCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import type { DashboardDefinition } from '../types/dashboard';

interface DashboardWizardProps {
  initialData?: DashboardDefinition | null;
  packageLevel: 'basic' | 'advanced' | 'executive';
  availableWorkTypes: { id: string; name: string }[];
  onSave: (dashboard: DashboardDefinition) => void;
  onCancel: () => void;
  userRole?: string;
}

// Canonical Options
const VISUALIZATION_OPTIONS = [
  { id: 'table', label: 'Table', icon: TableIcon },
  { id: 'bar_chart', label: 'Bar Chart', icon: BarChart3 },
  { id: 'line_chart', label: 'Line Chart', icon: LineChart },
  { id: 'kpi_tile', label: 'KPI Tile', icon: Shield },
];

const AUDIENCE_OPTIONS = [
    { id: 'agents', label: 'Agents' },
    { id: 'team_leads', label: 'Team Leads' },
    { id: 'managers', label: 'Managers' },
    { id: 'executives', label: 'Executives' }
];

const INTENT_OPTIONS = [
    { id: 'personal_work', label: 'Personal Work Tracking' },
    { id: 'team_queue', label: 'Team Queue Oversight' },
    { id: 'sla_risk', label: 'SLA Risk Monitoring' },
    { id: 'incident_awareness', label: 'Incident Awareness' }
];

const TIME_WINDOW_OPTIONS = [
    { id: 'real_time', label: 'Real-time / Today' },
    { id: 'last_7_days', label: 'Last 7 days' },
    { id: 'last_30_days', label: 'Last 30 days' }
];

const SLA_FOCUS_OPTIONS = [
    { id: 'breach_imminent', label: 'Breach Imminent (<2h)' },
    { id: 'breached', label: 'Breached' },
    { id: 'met', label: 'Met' }
];

const REGION_OPTIONS = [
    { id: 'amer', label: 'AMER (Americas)' },
    { id: 'emea', label: 'EMEA (Europe, Middle East, Africa)' },
    { id: 'apac', label: 'APAC (Asia Pacific)' },
    { id: 'global', label: 'Global / Follow-the-Sun' }
];

export function DashboardWizard({ 
  initialData, 
  packageLevel, 
  availableWorkTypes,
  onSave, 
  onCancel,
  userRole = 'consultant'
}: DashboardWizardProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<DashboardDefinition>(initialData || {
    id: `dash_${Date.now()}`,
    name: '',
    description: '',
    audience: [], // DASH_B_01
    scope: [],    // DASH_B_02
    visualizations: [], // DASH_B_05
    status: 'draft',
    locked: false,
    package: packageLevel,
    advanced: packageLevel === 'advanced' ? {
        regions: [],
        slaFocus: []
    } : undefined
  });

  // Ensure package consistency
  useEffect(() => {
      if (!initialData) {
          setFormData(prev => ({ 
              ...prev, 
              package: packageLevel,
              advanced: packageLevel === 'advanced' ? (prev.advanced || { regions: [], slaFocus: [] }) : undefined
          }));
      }
  }, [packageLevel, initialData]);

  const updateField = (field: keyof DashboardDefinition, value: any) => {
    if (formData.locked && userRole === 'client') return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAdvanced = (field: string, value: any) => {
      if (formData.locked && userRole === 'client') return;
      setFormData(prev => ({
          ...prev,
          advanced: {
              ...prev.advanced,
              [field]: value
          }
      }));
  };

  const toggleSelection = (field: 'scope' | 'visualizations' | 'audience', item: string) => {
    if (formData.locked && userRole === 'client') return;
    setFormData(prev => {
      const current = prev[field] as string[];
      const exists = current.includes(item);
      return {
        ...prev,
        [field]: exists ? current.filter(i => i !== item) : [...current, item]
      };
    });
  };

  const toggleAdvancedSelection = (field: 'regions' | 'slaFocus', item: string) => {
      if (formData.locked && userRole === 'client') return;
      setFormData(prev => {
          const current = (prev.advanced?.[field] as string[]) || [];
          const exists = current.includes(item);
          const newArray = exists ? current.filter(i => i !== item) : [...current, item];
          return {
              ...prev,
              advanced: {
                  ...prev.advanced,
                  [field]: newArray
              }
          };
      });
  };

  const handleNext = () => {
    // Validation
    if (step === 1) {
        if (!formData.name) { toast.error("Dashboard Name is required"); return; }
        if (formData.audience.length === 0) { toast.error("Select at least one Audience"); return; }
    }
    if (step === 2) {
        if (formData.scope.length === 0) { toast.error("Select Primary Work Focus"); return; }
        if (!formData.intent) { toast.error("Operational Intent is required"); return; }
        if (!formData.timeWindow) { toast.error("Time Horizon is required"); return; }
    }
    if (step === 3) {
        if (formData.visualizations.length === 0) { toast.error("Select at least one Visualization Type"); return; }
        // Advanced validation could go here if specific fields were mandatory
    }

    setStep(step + 1);
  };

  const handleSave = () => {
    if (!formData.name) return;
    onSave(formData);
  };

  const toggleLock = () => {
      const newStatus = formData.locked ? 'draft' : 'ready_for_build';
      setFormData(prev => ({ 
          ...prev, 
          locked: !prev.locked,
          status: newStatus,
          lockedAt: !prev.locked ? new Date().toISOString() : undefined
      }));
  };

  const isLocked = formData.locked;

  // Guidance Component
  const Guidance = ({ title, text }: { title: string, text: string }) => (
      <HoverCard>
          <HoverCardTrigger asChild>
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help ml-2 inline-block align-text-bottom" />
          </HoverCardTrigger>
          <HoverCardContent className="w-80 bg-slate-900 text-slate-50 border-slate-800">
              <div className="flex justify-between space-x-4">
                  <div className="space-y-1">
                      <h4 className="text-sm font-semibold">{title}</h4>
                      <p className="text-xs opacity-90">{text}</p>
                  </div>
              </div>
          </HoverCardContent>
      </HoverCard>
  );

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="border-b px-8 py-5 flex justify-between items-center bg-white shadow-sm z-10">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 text-indigo-600" />
              {initialData ? 'Edit Dashboard Specification' : 'New Dashboard Specification'}
          </h2>
          <div className="flex items-center gap-2 text-sm mt-1">
             <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100 uppercase tracking-wider text-[10px] font-semibold">
                {packageLevel} Package
             </Badge>
             <span className="text-slate-400">•</span>
             <span className="text-slate-500">Canonical Question Library v2026.2</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
            {isLocked && <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200"><Lock className="w-3 h-3 mr-1"/> Ready for Build</Badge>}
            <div className="flex items-center gap-1 text-sm font-medium text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                <span>Step {step}</span>
                <span className="text-slate-300">/</span>
                <span>{packageLevel === 'advanced' ? 4 : 4}</span>
            </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* STEP 1: IDENTITY & AUDIENCE */}
          {step === 1 && (
            <Card className="border-t-4 border-t-indigo-600 shadow-sm">
              <CardHeader>
                <CardTitle>Identity & Audience</CardTitle>
                <CardDescription>Define the core purpose and who this dashboard serves.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 space-y-2">
                        <Label>Dashboard Name <span className="text-red-500">*</span></Label>
                        <Input 
                            value={formData.name} 
                            onChange={(e) => updateField('name', e.target.value)}
                            disabled={isLocked}
                            placeholder="e.g., Incident Operational Overview"
                            className="text-lg font-medium"
                        />
                    </div>
                    
                    <div className="space-y-2">
                        <Label>Audience <span className="text-red-500">*</span>
                            <Guidance title="Audience" text="Audience determines permissions, filters, and visualization density. Who is this for?" />
                        </Label>
                        <div className="space-y-2 border rounded-md p-3 bg-white">
                            {AUDIENCE_OPTIONS.map(opt => (
                                <div key={opt.id} className="flex items-center space-x-2">
                                    <Checkbox 
                                        id={`aud-${opt.id}`} 
                                        checked={formData.audience.includes(opt.id)}
                                        onCheckedChange={() => toggleSelection('audience', opt.id)}
                                        disabled={isLocked}
                                    />
                                    <Label htmlFor={`aud-${opt.id}`} className="font-normal cursor-pointer">{opt.label}</Label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={formData.description || ''} 
                    onChange={(e) => updateField('description', e.target.value)}
                    disabled={isLocked}
                    placeholder="What specific questions does this dashboard answer?"
                    className="h-24 resize-none"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 2: SCOPE & INTENT */}
          {step === 2 && (
            <Card className="border-t-4 border-t-indigo-600 shadow-sm">
              <CardHeader>
                <CardTitle>Scope & Intent</CardTitle>
                <CardDescription>Define the data boundaries and operational goal.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* Primary Work Focus */}
                <div className="space-y-3">
                    <Label className="text-base font-semibold">Primary Work Focus <span className="text-red-500">*</span>
                        <Guidance title="Primary Work Focus" text="Dashboards may span multiple work types but must declare scope explicitly." />
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                        {availableWorkTypes.map(wt => (
                            <div key={wt.id} className={`flex items-center space-x-2 border p-3 rounded-md transition-colors ${formData.scope.includes(wt.id) ? 'bg-indigo-50 border-indigo-200' : 'bg-white'}`}>
                                <Checkbox 
                                    id={`scope-${wt.id}`} 
                                    checked={formData.scope.includes(wt.id)}
                                    onCheckedChange={() => toggleSelection('scope', wt.id)}
                                    disabled={isLocked}
                                />
                                <Label htmlFor={`scope-${wt.id}`} className="font-medium cursor-pointer flex-1">{wt.name}</Label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Operational Intent */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Operational Intent <span className="text-red-500">*</span>
                            <Guidance title="Operational Intent" text="Intent drives filters, JQL construction, and chart selection." />
                        </Label>
                        <Select 
                            value={formData.intent} 
                            onValueChange={(v: any) => updateField('intent', v)}
                            disabled={isLocked}
                        >
                            <SelectTrigger className="h-12">
                                <SelectValue placeholder="Select intent..." />
                            </SelectTrigger>
                            <SelectContent>
                                {INTENT_OPTIONS.map(opt => (
                                    <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Time Horizon */}
                    <div className="space-y-3">
                        <Label className="text-base font-semibold">Time Horizon <span className="text-red-500">*</span>
                            <Guidance title="Time Horizon" text="Longer horizons increase query complexity and may impact performance." />
                        </Label>
                        <Select 
                            value={formData.timeWindow} 
                            onValueChange={(v: any) => updateField('timeWindow', v)}
                            disabled={isLocked}
                        >
                            <SelectTrigger className="h-12">
                                <SelectValue placeholder="Select timeframe..." />
                            </SelectTrigger>
                            <SelectContent>
                                {TIME_WINDOW_OPTIONS.map(opt => (
                                    <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

              </CardContent>
            </Card>
          )}

          {/* STEP 3: VISUALIZATION & ADVANCED */}
          {step === 3 && (
            <div className="space-y-6">
                <Card className="border-t-4 border-t-indigo-600 shadow-sm">
                  <CardHeader>
                    <CardTitle>Visualizations</CardTitle>
                    <CardDescription>How should this data be presented?</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Visualization Types */}
                    <Label className="text-base font-semibold">Visualization Types <span className="text-red-500">*</span>
                        <Guidance title="Visualizations" text="Exact gadgets are implementation details handled later." />
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {VISUALIZATION_OPTIONS.map(viz => (
                            <div 
                                key={viz.id} 
                                className={`
                                    border-2 rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer transition-all hover:shadow-md
                                    ${formData.visualizations.includes(viz.id as any) ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white border-slate-200 hover:border-slate-300'}
                                    ${isLocked ? 'opacity-70 cursor-not-allowed' : ''}
                                `}
                                onClick={() => !isLocked && toggleSelection('visualizations', viz.id)}
                            >
                                <viz.icon className="w-8 h-8" />
                                <span className="font-semibold text-sm">{viz.label}</span>
                            </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                {/* ADVANCED PACKAGE QUESTIONS */}
                {packageLevel === 'advanced' && (
                    <Card className="border-t-4 border-t-amber-500 shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-amber-500" />
                                Advanced Configuration
                            </CardTitle>
                            <CardDescription>Enterprise Configuration Options</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            
                            {/* Cross-Team Visibility */}
                            <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
                                <div className="space-y-0.5">
                                    <Label className="text-base">Cross-Team Visibility</Label>
                                    <p className="text-sm text-gray-500">Enables shared filters, team normalization, and permission modeling.</p>
                                </div>
                                <Switch 
                                    checked={formData.advanced?.crossTeam}
                                    onCheckedChange={(c) => updateAdvanced('crossTeam', c)}
                                    disabled={isLocked}
                                />
                            </div>

                            {/* Regional Segmentation */}
                            <div className="space-y-3 p-4 border rounded-lg bg-white">
                                <Label className="text-base">Regional Segmentation</Label>
                                <p className="text-sm text-gray-500 mb-2">Applies to Follow-the-Sun and global support models.</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {REGION_OPTIONS.map(opt => (
                                        <div key={opt.id} className="flex items-center space-x-2">
                                            <Checkbox 
                                                id={`reg-${opt.id}`}
                                                checked={formData.advanced?.regions?.includes(opt.id)}
                                                onCheckedChange={() => toggleAdvancedSelection('regions', opt.id)}
                                                disabled={isLocked}
                                            />
                                            <Label htmlFor={`reg-${opt.id}`} className="font-normal cursor-pointer">{opt.label}</Label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* SLA Focus */}
                            <div className="space-y-3 p-4 border rounded-lg bg-white">
                                <Label className="text-base">SLA Focus</Label>
                                <p className="text-sm text-gray-500 mb-2">Clarification: SLA logic inherits global SLA configuration.</p>
                                <div className="flex flex-wrap gap-4">
                                    {SLA_FOCUS_OPTIONS.map(opt => (
                                        <div key={opt.id} className="flex items-center space-x-2">
                                            <Checkbox 
                                                id={`sla-${opt.id}`}
                                                checked={formData.advanced?.slaFocus?.includes(opt.id)}
                                                onCheckedChange={() => toggleAdvancedSelection('slaFocus', opt.id)}
                                                disabled={isLocked}
                                            />
                                            <Label htmlFor={`sla-${opt.id}`} className="font-normal cursor-pointer">{opt.label}</Label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Service-Level Breakdown & Trend Analysis */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
                                    <div className="space-y-0.5">
                                        <Label className="text-base">Service-Level Breakdown</Label>
                                        <p className="text-xs text-gray-500">Requires Service definitions to exist.</p>
                                    </div>
                                    <Switch 
                                        checked={formData.advanced?.byService}
                                        onCheckedChange={(c) => updateAdvanced('byService', c)}
                                        disabled={isLocked}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 border rounded-lg bg-white">
                                    <div className="space-y-0.5">
                                        <Label className="text-base">Trend Analysis</Label>
                                        <p className="text-xs text-gray-500">Volume trends, MTTR trends, etc.</p>
                                    </div>
                                    <Switch 
                                        checked={formData.advanced?.trends}
                                        onCheckedChange={(c) => updateAdvanced('trends', c)}
                                        disabled={isLocked}
                                    />
                                </div>
                            </div>

                        </CardContent>
                    </Card>
                )}
            </div>
          )}

          {/* STEP 4: REVIEW */}
          {step === 4 && (
            <div className="space-y-6">
                <Card className="shadow-sm">
                    <CardHeader>
                        <CardTitle>Review Specification</CardTitle>
                        <CardDescription>Verify dashboard requirements before locking for build.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 space-y-4">
                            <div className="grid grid-cols-2 gap-6 text-sm">
                                <div>
                                    <span className="text-slate-500 block text-xs uppercase font-semibold">Dashboard Name</span>
                                    <span className="font-medium text-lg text-slate-900">{formData.name}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs uppercase font-semibold">Audience</span>
                                    <span className="font-medium">{formData.audience.map(a => AUDIENCE_OPTIONS.find(o => o.id === a)?.label).join(', ') || 'None'}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs uppercase font-semibold">Primary Work Focus</span>
                                    <span className="font-medium">
                                        {formData.scope.map(s => availableWorkTypes.find(wt => wt.id === s)?.name || s).join(', ') || 'None'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs uppercase font-semibold">Operational Intent</span>
                                    <span className="font-medium">{INTENT_OPTIONS.find(o => o.id === formData.intent)?.label || 'Not Selected'}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs uppercase font-semibold">Time Horizon</span>
                                    <span className="font-medium">{TIME_WINDOW_OPTIONS.find(o => o.id === formData.timeWindow)?.label || 'Not Selected'}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block text-xs uppercase font-semibold">Visualizations</span>
                                    <div className="flex gap-2 mt-1">
                                        {formData.visualizations.map(v => (
                                            <Badge key={v} variant="outline" className="bg-white">{VISUALIZATION_OPTIONS.find(o => o.id === v)?.label}</Badge>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {packageLevel === 'advanced' && (
                                <div className="border-t border-slate-200 pt-4 mt-4">
                                    <h4 className="text-xs uppercase font-semibold text-amber-600 mb-3">Advanced Config</h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className={`w-4 h-4 ${formData.advanced?.crossTeam ? 'text-green-600' : 'text-slate-300'}`} />
                                            <span>Cross-Team Visibility</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className={`w-4 h-4 ${formData.advanced?.byService ? 'text-green-600' : 'text-slate-300'}`} />
                                            <span>Service-Level Breakdown</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className={`w-4 h-4 ${formData.advanced?.trends ? 'text-green-600' : 'text-slate-300'}`} />
                                            <span>Trend Analysis</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {!isLocked && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-sm text-blue-800">
                                <BookOpen className="w-5 h-5 shrink-0" />
                                <div>
                                    <strong>Canonical Statement:</strong>
                                    <p className="mt-1 opacity-90">"The Dashboard Question Library defines intent, scope, and accountability. Implementation tooling translates that intent into Jira artifacts."</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="flex items-center justify-between bg-slate-100 p-6 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${isLocked ? 'bg-green-100 text-green-600' : 'bg-slate-200 text-slate-500'}`}>
                            <Lock className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="font-bold text-gray-900">Lock Specification</div>
                            <div className="text-sm text-slate-500">
                                {isLocked ? 'This dashboard is locked and Ready for Build.' : 'Mark this dashboard as authoritative and final.'}
                            </div>
                        </div>
                    </div>
                    <Switch checked={isLocked} onCheckedChange={toggleLock} className="scale-125" />
                </div>
            </div>
          )}

        </div>
      </div>

      <div className="border-t bg-white px-8 py-5 flex justify-between items-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <div className="flex gap-3">
            {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>
            )}
            {step < 4 ? (
                <Button onClick={handleNext} className="bg-indigo-600 hover:bg-indigo-700 min-w-[100px]">Next <ArrowRight className="w-4 h-4 ml-2" /></Button>
            ) : (
                <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700 min-w-[140px]">
                    <Save className="w-4 h-4 mr-2" /> Save & Close
                </Button>
            )}
        </div>
      </div>
    </div>
  );
}
